import React, { useState, useEffect, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { QanunLogo, QanunLogoMinimal } from '@/components/QanunLogo';
import { 
  MessageSquare, FileText, Search, Bell, Mic, MicOff, 
  Building2, Users, Briefcase, Globe, Shield, ChevronDown,
  ArrowRight, Check, Play, Zap, BookOpen, X, TrendingUp,
  FileSearch, PenTool, Scale, Landmark, Clock, Flame, Sun, Moon,
  Upload, AlertTriangle, Home, Languages, Grid3X3, User
} from 'lucide-react';
import { toast } from 'sonner';

// STABLE Fade-in component - MUST be outside main component to prevent recreation
const FadeInSection = memo(function FadeInSection({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || hasAnimated.current) return;

    // Check if already in view on mount (generous: top within viewport + 100px)
    const rect = element.getBoundingClientRect();
    if (rect.top < window.innerHeight + 100) {
      hasAnimated.current = true;
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px 200px 0px' }
    );

    observer.observe(element);

    // Safety fallback: ensure section never stays invisible forever
    const fallback = setTimeout(() => {
      if (!hasAnimated.current) {
        hasAnimated.current = true;
        setIsVisible(true);
      }
    }, 2500);

    return () => { observer.disconnect(); clearTimeout(fallback); };
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
});

// Animated counter component - fixed  
function AnimatedCounter({ end, duration = 1500, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || hasAnimated.current) return;

    const startAnimation = () => {
      if (hasAnimated.current) return;
      hasAnimated.current = true;
      const startTime = Date.now();
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCount(Math.floor(eased * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      animate();
    };

    // Check if already in view on mount
    const rect = element.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      startAnimation();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startAnimation();
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { language, toggleLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [recognition, setRecognition] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [showDemo, setShowDemo] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [landingBilling, setLandingBilling] = useState('monthly');

  // Rotating words state
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [wordVisible, setWordVisible] = useState(true);
  const rotatingWords = [
    t('lawyers', 'المحامين'),
    t('founders', 'المؤسسين'),
    t('in-house counsel', 'المستشارين القانونيين'),
    t('expats', 'المغتربين')
  ];

  // Rotating words animation - smoother and faster
  useEffect(() => {
    const interval = setInterval(() => {
      setWordVisible(false);
      setTimeout(() => {
        setCurrentWordIndex((prev) => (prev + 1) % rotatingWords.length);
        setWordVisible(true);
      }, 150);
    }, 2500);
    return () => clearInterval(interval);
  }, [rotatingWords.length]);

  // Language toggle with transition
  const handleLanguageToggle = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      toggleLanguage();
      setTimeout(() => {
        setIsTransitioning(false);
      }, 200);
    }, 200);
  };

  const handleTryNow = () => {
    navigate('/chat');
  };

  const handleAskQuestion = (question) => {
    navigate('/chat', { state: { initialQuestion: question } });
  };

  const handleVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error(t('Voice input requires Chrome or Safari browser', 'يتطلب إدخال الصوت متصفح Chrome أو Safari'));
      return;
    }

    if (isListening && recognition) {
      recognition.stop();
      setIsListening(false);
      if (transcript.trim()) {
        navigate('/chat', { state: { initialQuestion: transcript.trim() } });
      }
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recog = new SpeechRecognition();
    
    recog.lang = language === 'ar' ? 'ar-AE' : 'en-US';
    recog.continuous = true;
    recog.interimResults = true;

    let finalTranscript = '';

    recog.onstart = () => {
      setIsListening(true);
      setTranscript('');
      toast.success(t('🎤 Listening... tap again when done', '🎤 جاري الاستماع... اضغط مجدداً عند الانتهاء'));
    };

    recog.onresult = (event) => {
      let interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }
      setTranscript((finalTranscript + interimTranscript).trim());
    };

    recog.onerror = (event) => {
      console.error('Speech error:', event.error);
      setIsListening(false);
      if (event.error !== 'aborted') {
        toast.error(t('Voice error. Please try again.', 'خطأ في الصوت. حاول مجدداً.'));
      }
    };

    recog.onend = () => {
      setIsListening(false);
    };

    setRecognition(recog);
    recog.start();
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Demo conversation data
  const demoConversation = [
    {
      type: 'user',
      text: t('Can I own 100% of a Dubai company as a foreigner?', 'هل يمكنني امتلاك 100% من شركة دبي كأجنبي؟')
    },
    {
      type: 'ai',
      text: t(
        'Yes, since June 2021. The UAE amended the Commercial Companies Law (Federal Decree-Law No. 26/2020) allowing 100% foreign ownership in most mainland activities. However, some strategic sectors still require local partnership.',
        'نعم، منذ يونيو 2021. عدّلت الإمارات قانون الشركات التجارية (المرسوم بقانون اتحادي رقم 26/2020) للسماح بملكية أجنبية 100% في معظم الأنشطة. بعض القطاعات الاستراتيجية لا تزال تتطلب شراكة محلية.'
      ),
      citation: t('Federal Decree-Law No. 26/2020, Article 10', 'المرسوم بقانون اتحادي رقم 26/2020، المادة 10')
    },
    {
      type: 'user',
      text: t('Which sectors require a local partner?', 'ما القطاعات التي تتطلب شريكاً محلياً؟')
    },
    {
      type: 'ai',
      text: t(
        'Strategic sectors requiring UAE national ownership include: (1) Oil & gas exploration, (2) Security services, (3) Banking & insurance (51% local ownership), (4) Hajj & Umrah services. For most other sectors, you can now establish with 100% foreign ownership.',
        'القطاعات الاستراتيجية التي تتطلب ملكية مواطن إماراتي تشمل: (1) التنقيب عن النفط والغاز، (2) خدمات الأمن، (3) البنوك والتأمين (51% ملكية محلية)، (4) خدمات الحج والعمرة. لمعظم القطاعات الأخرى، يمكنك الآن التأسيس بملكية أجنبية 100%.'
      ),
      citation: t('Cabinet Resolution No. 55/2021', 'قرار مجلس الوزراء رقم 55/2021')
    }
  ];

  // Trending questions data
  const trendingQuestions = [
    {
      question: t('What is the new UAE Golden Visa eligibility?', 'ما هي شروط التأشيرة الذهبية الجديدة؟'),
      category: t('Immigration', 'الهجرة'),
    },
    {
      question: t('How to calculate end-of-service gratuity?', 'كيف أحسب مكافأة نهاية الخدمة؟'),
      category: t('Employment', 'التوظيف'),
    },
    {
      question: t('DIFC vs ADGM: Which is better for fintech?', 'DIFC أم ADGM: أيهما أفضل للتقنية المالية؟'),
      category: t('Free Zones', 'المناطق الحرة'),
    },
    {
      question: t('What are the new UAE labor law changes in 2024?', 'ما تغييرات قانون العمل الإماراتي 2024؟'),
      category: t('Employment', 'التوظيف'),
    },
    {
      question: t('Can I break my tenancy contract early?', 'هل يمكنني إنهاء عقد الإيجار مبكراً؟'),
      category: t('Real Estate', 'العقارات'),
    }
  ];

  // Auto-advance demo
  useEffect(() => {
    if (showDemo && demoStep < demoConversation.length) {
      const timer = setTimeout(() => {
        setDemoStep(prev => prev + 1);
      }, demoStep === 0 ? 1000 : 2500);
      return () => clearTimeout(timer);
    }
  }, [showDemo, demoStep]);

  const resetDemo = () => {
    setDemoStep(0);
    setShowDemo(false);
  };

  return (
    <div 
      className={`min-h-screen transition-all duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'} bg-white dark:bg-gray-950 text-gray-900 dark:text-white`} 
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      {/* Live Demo Modal */}
      {showDemo && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl animate-scale-in">
            <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="font-semibold text-gray-900 dark:text-white">{t('Live Demo', 'عرض مباشر')}</span>
              </div>
              <button 
                onClick={resetDemo}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors cursor-pointer"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
              {demoConversation.slice(0, demoStep).map((msg, idx) => (
                <div 
                  key={idx}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
                >
                  <div className={`max-w-[80%] ${msg.type === 'user' ? 'order-1' : ''}`}>
                    {msg.type === 'ai' && (
                      <div className="flex items-center mb-2">
                        <div className="w-7 h-7 bg-gray-900 rounded-full flex items-center justify-center">
                          <QanunLogoMinimal className="w-4 h-4 text-white" />
                        </div>
                        <span className="ms-2 text-sm font-medium text-gray-600 dark:text-gray-400">Qanun AI</span>
                      </div>
                    )}
                    <div className={`px-4 py-3 rounded-2xl ${
                      msg.type === 'user' 
                        ? 'bg-gray-900 text-white' 
                        : 'bg-gray-50 text-gray-900'
                    }`}>
                      <p className="text-sm leading-relaxed">{msg.text}</p>
                    </div>
                    {msg.citation && (
                      <div className="mt-2 ms-9 flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <BookOpen className="w-3 h-3 me-1" />
                        <span>{msg.citation}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {demoStep < demoConversation.length && (
                <div className="flex justify-start">
                  <div className="bg-gray-50 rounded-2xl px-4 py-3 flex items-center space-x-2 rtl:space-x-reverse">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
              {demoStep >= demoConversation.length ? (
                <Button 
                  onClick={() => {
                    resetDemo();
                    handleTryNow();
                  }}
                  className="w-full btn-primary rounded-full py-6"
                >
                  {t('Try It Yourself', 'جرّبه بنفسك')}
                  <ArrowRight className="w-5 h-5 ms-2 rtl:rotate-180" />
                </Button>
              ) : (
                <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                  {t('Watch how Qanun AI answers legal questions...', 'شاهد كيف يجيب قانون AI على الأسئلة القانونية...')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 dark:bg-gray-950/95 backdrop-blur-xl border-b border-gray-100 dark:border-gray-800 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            <QanunLogo size="default" showText={true} />
            
            <div className="hidden md:flex items-center space-x-8 rtl:space-x-reverse">
              <button onClick={() => scrollToSection('features')} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer">
                {t('Features', 'المميزات')}
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer">
                {t('Pricing', 'الأسعار')}
              </button>
              <button onClick={() => scrollToSection('faq')} className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer">
                {t('About', 'عن قانون')}
              </button>
            </div>

            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <button
                onClick={toggleTheme}
                data-testid="landing-theme-toggle"
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 transition-colors cursor-pointer"
                title={theme === 'dark' ? t('Light mode', 'الوضع الفاتح') : t('Dark mode', 'الوضع الداكن')}
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button
                onClick={handleLanguageToggle}
                data-testid="nav-language-toggle"
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors flex items-center cursor-pointer"
              >
                <Globe className="w-4 h-4 me-1" />
                {language === 'en' ? 'العربية' : 'EN'}
              </button>
              <Button
                onClick={handleTryNow}
                data-testid="nav-try-button"
                className="btn-primary text-sm px-5 py-2 rounded-full"
                title={t('Free. No signup required.', 'مجاني. بدون تسجيل.')}
              >
                {t('Try Qanun AI Free', 'جرّب قانون AI مجاناً')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold text-gray-900 dark:text-white mb-6 tracking-tight leading-[1.1]">
            {t('UAE law. Instant answers.', 'قانون الإمارات. إجابات فورية.')}
            <br />
            {t('Every citation.', 'كل مرجع.')}
          </h1>
          
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-4 leading-relaxed">
            {t(
              "The AI legal intelligence platform built for UAE lawyers, in-house counsel, and ambitious founders. Trained on DIFC, ADGM, UAE Federal Law, and 45+ free zone regulations. Every answer cites the exact Article, Regulation, or Court Judgment it's based on.",
              'منصة الذكاء القانوني المصممة للمحامين الإماراتيين والمستشارين القانونيين والمؤسسين الطموحين. مُدرَّبة على قوانين مركز دبي المالي وسوق أبوظبي والقانون الاتحادي وأكثر من 45 منطقة حرة. كل إجابة تستشهد بالمادة أو اللائحة أو الحكم القضائي بالضبط.'
            )}
          </p>
          
          {/* Rotating text */}
          <p className="text-xl text-gray-900 dark:text-white mb-10">
            <span className="font-semibold">{t('Built for ', 'مصمم لـ')}</span>
            <span 
              className={`text-blue-500 font-semibold transition-opacity duration-300 ${wordVisible ? 'opacity-100' : 'opacity-0'}`}
            >
              {rotatingWords[currentWordIndex]}
            </span>
            <span className="font-semibold">{t(' in the UAE.', ' في الإمارات.')}</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button
              size="lg"
              onClick={handleTryNow}
              data-testid="hero-try-button"
              className="btn-primary px-8 py-6 text-lg font-medium rounded-full"
            >
              {t('Ask Qanun AI Anything', 'اسأل قانون AI أي شيء')}
              <ArrowRight className="w-5 h-5 ms-2 rtl:rotate-180" />
            </Button>
            <a
              href="mailto:virajdhawan@gmail.com?subject=Qanun AI Demo Request"
              data-testid="hero-demo-button"
              className="inline-flex items-center px-8 py-6 text-lg font-medium rounded-full border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 border-2"
            >
              <Play className="w-5 h-5 me-2" />
              {t('Request a Demo', 'اطلب عرضاً توضيحياً')}
            </a>
          </div>

          {/* Voice Input */}
          <div className="flex flex-col items-center mb-8">
            <button
              onClick={handleVoiceInput}
              data-testid="hero-voice-button"
              className={`flex items-center space-x-2 rtl:space-x-reverse px-6 py-3 rounded-full transition-all cursor-pointer ${
                isListening 
                  ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-2 border-red-200 dark:border-red-800 animate-pulse' 
                  : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              <span className="text-sm font-medium">
                {isListening 
                  ? t('Tap to send your question', 'اضغط لإرسال سؤالك')
                  : t('Or ask with your voice', 'أو اسأل بصوتك')
                }
              </span>
            </button>

            {transcript && (
              <div className="mt-4 max-w-xl w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 animate-fade-in">
                <p className="text-sm text-gray-500 mb-1">{t('Your question:', 'سؤالك:')}</p>
                <p className="text-gray-900 dark:text-white font-medium">{transcript}</p>
                {isListening && (
                  <p className="text-xs text-gray-400 mt-2">{t('Tap the mic button to send', 'اضغط زر الميكروفون للإرسال')}</p>
                )}
              </div>
            )}
          </div>

          {/* Trusted By Strip */}
          <div className="border-t border-b border-gray-200 dark:border-gray-800 py-6 mt-8">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              {t('Trusted by legal professionals across the UAE', 'موثوق من قبل المهنيين القانونيين في جميع أنحاء الإمارات')}
            </p>
          </div>
        </div>
      </section>

      {/* Live Stats Bar */}
      <section className="py-12 px-6 border-t border-b border-blue-500/20 bg-white dark:bg-gray-950">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: 650, suffix: '+', label: t('Legal Sources Indexed', 'مصدر قانوني مفهرس') },
              { number: 45, suffix: '+', label: t('Free Zones Covered', 'منطقة حرة مغطاة') },
              { number: 3, suffix: '', label: t('Core Jurisdictions', 'اختصاصات أساسية') },
              { number: 200, suffix: '+', label: t('Nationalities Supported', 'جنسية مدعومة') }
            ].map((stat, idx) => (
              <FadeInSection key={idx} delay={idx * 100}>
                <div>
                  <p className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-2">
                    <AnimatedCounter end={stat.number} duration={1500} suffix={stat.suffix} />
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Trending Questions Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-white to-gray-50 dark:from-gray-950 dark:to-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t('Trending Questions', 'الأسئلة الرائجة')}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('What people are asking right now', 'ما يسأله الناس الآن')}
                </p>
              </div>
            </div>
            <button
              onClick={handleTryNow}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center cursor-pointer"
            >
              {t('View all', 'عرض الكل')}
              <ArrowRight className="w-4 h-4 ms-1 rtl:rotate-180" />
            </button>
          </div>

          <div className="grid md:grid-cols-5 gap-3">
            {trendingQuestions.map((item, idx) => (
              <FadeInSection key={idx} delay={idx * 100}>
                <button
                  onClick={() => handleAskQuestion(item.question)}
                  className="group p-4 bg-white border border-gray-100 rounded-xl hover:border-gray-300 hover:shadow-md transition-all text-left rtl:text-right w-full cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                      {item.category}
                    </span>
                  </div>
                  <p className="text-sm text-gray-900 group-hover:text-gray-700 line-clamp-2">
                    {item.question}
                  </p>
                </button>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-24 px-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
              {t('THE PROBLEM', 'المشكلة')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 dark:text-white mb-4 tracking-tight">
              {t('Legal advice costs ', 'الاستشارة القانونية تكلف ')}
              <span className="text-blue-500 font-bold">AED <AnimatedCounter end={2000} duration={1500} /></span>
              {t('/hour.', ' درهم/ساعة.')}
            </h2>
            <p className="text-2xl text-gray-600 dark:text-gray-300 font-light">
              {t("Understanding your rights shouldn't.", 'فهم حقوقك لا يجب أن يكلف ذلك.')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Briefcase,
                heading: t('"Is this employment contract legal?"', '"هل هذا العقد الوظيفي قانوني؟"'),
                body: t('Signing something you don\'t understand is scary.', 'توقيع شيء لا تفهمه أمر مخيف.')
              },
              {
                icon: Building2,
                heading: t('"Which free zone is right for my business?"', '"أي منطقة حرة مناسبة لعملي؟"'),
                body: t('There are 45+ options. Google won\'t tell you the real answer.', 'هناك أكثر من 45 خياراً. جوجل لن يخبرك بالإجابة الحقيقية.')
              },
              {
                icon: Landmark,
                heading: t('"What if my landlord doesn\'t refund my deposit?"', '"ماذا لو لم يُعِد المالك التأمين؟"'),
                body: t('Your lease is in English and Arabic. The laws conflict. Now what?', 'عقدك بالإنجليزية والعربية. القوانين متضاربة. ماذا الآن؟')
              }
            ].map((card, idx) => {
              const Icon = card.icon;
              return (
                <FadeInSection key={idx} delay={idx * 100}>
                  <Card className="p-8 bg-white dark:bg-gray-800 border-0 shadow-sm rounded-2xl hover:shadow-md transition-shadow cursor-pointer">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-gray-900 dark:text-white" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{card.heading}</h3>
                    <p className="text-gray-600 dark:text-gray-300">{card.body}</p>
                  </Card>
                </FadeInSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* What is Qanun AI Section */}
      <section className="py-24 px-6 bg-white dark:bg-gray-950">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
            {t('WHAT IS QANUN AI', 'ما هو قانون AI')}
          </p>
          <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 dark:text-white mb-4 tracking-tight">
            {t('Your virtual senior counsel.', 'مستشارك القانوني الافتراضي.')}
            <br />
            {t('Trained on UAE law.', 'مُدرَّب على قانون الإمارات.')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed mb-6 font-light">
            {t(
              'Qanun AI is an AI platform trained specifically on ADGM, DIFC, and UAE Federal law. Think of it as having a senior UAE legal researcher available 24/7 — who speaks your language, answers instantly, and costs nothing.',
              'قانون AI هو منصة ذكاء اصطناعي مُدرَّبة خصيصاً على قوانين سوق أبوظبي العالمي ومركز دبي المالي والقانون الاتحادي الإماراتي. فكّر به كباحث قانوني إماراتي متمرس متاح على مدار الساعة — يتحدث لغتك، يجيب فوراً، ومجاناً.'
            )}
          </p>
          <p className="text-lg text-gray-900 dark:text-white font-medium">
            {t(
              "You'll still need lawyers for the complex stuff. But now you know what you're dealing with.",
              'ستظل بحاجة للمحامين للأمور المعقدة. لكن الآن تعرف ما الذي تتعامل معه.'
            )}
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 px-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
              {t('HOW IT WORKS', 'كيف يعمل')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 dark:text-white tracking-tight">
              {t('Three steps to legal clarity.', 'ثلاث خطوات للوضوح القانوني.')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                number: '1',
                title: t('Ask in plain language', 'اسأل بلغة بسيطة'),
                body: t(
                  'Just type your legal question in Arabic or English. No jargon required — ask like you would ask a friend.',
                  'فقط اكتب سؤالك القانوني بالعربية أو الإنجليزية. لا حاجة للمصطلحات — اسأل كما تسأل صديقاً.'
                )
              },
              {
                number: '2',
                title: t('Qanun AI analyzes UAE law', 'قانون AI يحلل القانون الإماراتي'),
                body: t(
                  'We search across ADGM regulations, DIFC court judgments, Federal laws, and all 45 free zones — in seconds.',
                  'نبحث عبر لوائح سوق أبوظبي العالمي، وأحكام محاكم مركز دبي المالي، والقوانين الاتحادية، وجميع المناطق الحرة الـ45 — في ثوانٍ.'
                )
              },
              {
                number: '3',
                title: t('Get an instant answer with citations', 'احصل على إجابة فورية مع المراجع'),
                body: t(
                  'Every response includes the exact law, regulation, or judgment that supports it. No guessing. No hallucinations.',
                  'كل إجابة تتضمن القانون أو اللائحة أو الحكم الدقيق الذي يدعمها. بدون تخمين. بدون هلوسات.'
                )
              }
            ].map((step, idx) => (
              <FadeInSection key={idx} delay={idx * 150} className="text-center">
                <div className="w-14 h-14 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-2xl flex items-center justify-center text-2xl font-semibold mx-auto mb-6">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">{step.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{step.body}</p>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Document Review Section */}
      <section className="py-24 px-6 bg-white dark:bg-gray-950">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            {/* Left Content (3 columns) */}
            <div className="lg:col-span-3">
              <p className="text-sm font-semibold text-blue-500 uppercase tracking-wider mb-4">
                {t('DOCUMENT REVIEW', 'مراجعة المستندات')}
              </p>
              <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 dark:text-white mb-6 tracking-tight">
                {t('Upload your contract.', 'ارفع عقدك.')}
                <br />
                {t('Know your risk in 60 seconds.', 'اعرف مخاطرك في 60 ثانية.')}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                {t(
                  "Drag and drop any UAE contract — employment agreement, lease, shareholder agreement, SPA — and Qanun AI will identify every clause that conflicts with UAE law, flag missing mandatory provisions, and tell you exactly what to negotiate.",
                  'اسحب وأفلت أي عقد إماراتي — عقد توظيف، إيجار، اتفاقية مساهمين، عقد بيع — وسيحدد قانون AI كل بند يتعارض مع قانون الإمارات، ويشير للأحكام الإلزامية المفقودة، ويخبرك بالضبط ما يجب التفاوض عليه.'
                )}
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  t('Supports English and Arabic contracts', 'يدعم العقود بالإنجليزية والعربية'),
                  t('Flags clauses that violate DIFC, ADGM, or Federal law', 'يشير للبنود التي تخالف قوانين مركز دبي أو أبوظبي أو الاتحادي'),
                  t('Identifies missing mandatory provisions (e.g. gratuity, notice period)', 'يحدد الأحكام الإلزامية المفقودة (مثل مكافأة نهاية الخدمة، فترة الإشعار)'),
                  t('Highlights negotiation leverage points', 'يبرز نقاط القوة التفاوضية'),
                  t('Cites the exact law each flag is based on', 'يستشهد بالقانون الدقيق لكل تنبيه')
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-blue-500 shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{item}</span>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => navigate('/documents')}
                className="btn-primary px-8 py-4 text-lg rounded-full"
              >
                {t('Upload a Contract Free', 'ارفع عقداً مجاناً')}
                <ArrowRight className="w-5 h-5 ms-2 rtl:rotate-180" />
              </Button>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                {t('Your documents never leave the UAE. Hosted on Azure UAE North.', 'مستنداتك لا تغادر الإمارات أبداً. مستضافة على Azure UAE North.')}
              </p>
            </div>

            {/* Right Mockup (2 columns) */}
            <div className="lg:col-span-2">
              <div className="bg-gray-900 dark:bg-gray-800 rounded-2xl p-6 border border-gray-700">
                {/* Upload Zone */}
                <div className="border-2 border-dashed border-gray-600 rounded-xl p-8 text-center mb-4">
                  <Upload className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400 mb-2">{t('Drop your contract here', 'أسقط عقدك هنا')}</p>
                  <div className="flex justify-center gap-2">
                    {['PDF', 'DOCX', 'Arabic PDF'].map((format, idx) => (
                      <span key={idx} className="px-2 py-1 bg-gray-800 text-gray-500 text-xs rounded">
                        {format}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Mock Analysis Results */}
                <div className="bg-gray-800/50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-3">{t('Analysis Preview', 'معاينة التحليل')}</p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 text-sm">
                      <span className="w-2 h-2 mt-1.5 rounded-full bg-red-500 shrink-0" />
                      <span className="text-gray-300">⚠ Clause 7.2 — {t('Gratuity calculation does not comply with UAE Labour Law Article 51', 'حساب مكافأة نهاية الخدمة لا يتوافق مع المادة 51 من قانون العمل')}</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm">
                      <span className="w-2 h-2 mt-1.5 rounded-full bg-amber-500 shrink-0" />
                      <span className="text-gray-300">{t('Missing: Mandatory arbitration clause required for DIFC', 'مفقود: بند التحكيم الإلزامي المطلوب لمركز دبي المالي')}</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm">
                      <span className="w-2 h-2 mt-1.5 rounded-full bg-blue-500 shrink-0" />
                      <span className="text-gray-300">{t('Note: Non-compete clause may be unenforceable', 'ملاحظة: بند عدم المنافسة قد يكون غير قابل للتنفيذ')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-24 px-6 bg-white dark:bg-gray-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
              {t('REAL QUESTIONS, REAL ANSWERS', 'أسئلة حقيقية، إجابات حقيقية')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 dark:text-white tracking-tight mb-4">
              {t('See what people are asking.', 'شاهد ما يسأله الناس.')}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 font-light">
              {t('These are actual questions answered by Qanun AI.', 'هذه أسئلة فعلية أجاب عنها قانون AI.')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {[
              {
                category: t('Employment', 'التوظيف'),
                question: t('Can my employer change my job title without my consent?', 'هل يمكن لصاحب العمل تغيير مسماي الوظيفي بدون موافقتي؟'),
                answer: t('Under UAE Labour Law Article 12, any material change to employment terms requires written consent. A unilateral job title change affecting duties or salary could constitute breach of contract.', 'بموجب المادة 12 من قانون العمل الإماراتي، يتطلب أي تغيير جوهري في شروط العمل موافقة خطية. قد يشكل تغيير المسمى الوظيفي من جانب واحد خرقاً للعقد.'),
                citation: t('UAE Labour Law, Article 12', 'قانون العمل الإماراتي، المادة 12')
              },
              {
                category: t('Real Estate', 'العقارات'),
                question: t('My landlord wants to increase rent by 30%. Is this legal?', 'يريد المالك زيادة الإيجار بنسبة 30%. هل هذا قانوني؟'),
                answer: t('Rent increases in Dubai are regulated by RERA\'s Rental Index. Increases above 20% are only permitted if current rent is 40%+ below market rate. Your landlord must provide 90 days notice.', 'تنظم مؤشر الإيجارات من ريرا زيادات الإيجار في دبي. الزيادات فوق 20% مسموحة فقط إذا كان الإيجار الحالي أقل بـ40%+ من سعر السوق.'),
                citation: t('RERA Decree No. 43/2013', 'مرسوم ريرا رقم 43/2013')
              },
              {
                category: t('Business Setup', 'تأسيس الأعمال'),
                question: t('Can I own 100% of a Dubai company as a foreigner?', 'هل يمكنني امتلاك 100% من شركة دبي كأجنبي؟'),
                answer: t('Yes, since June 2021. The UAE amended the Commercial Companies Law allowing 100% foreign ownership in most mainland activities. Some strategic sectors still require local partnership.', 'نعم، منذ يونيو 2021. عدّلت الإمارات قانون الشركات التجارية للسماح بملكية أجنبية 100% في معظم الأنشطة. بعض القطاعات الاستراتيجية لا تزال تتطلب شراكة محلية.'),
                citation: t('Federal Decree-Law No. 26/2020', 'مرسوم بقانون اتحادي رقم 26/2020')
              },
              {
                category: t('Visa & Immigration', 'التأشيرات والهجرة'),
                question: t('How long can I stay in the UAE after resignation?', 'كم يمكنني البقاء في الإمارات بعد الاستقالة؟'),
                answer: t('You have 30 days from your visa cancellation date to either find a new sponsor, exit the country, or change status. Overstaying incurs AED 100/day fines after a 30-day grace period.', 'لديك 30 يوماً من تاريخ إلغاء تأشيرتك للعثور على كفيل جديد أو المغادرة أو تغيير الحالة. التجاوز يكلف 100 درهم/يوم بعد فترة سماح 30 يوماً.'),
                citation: t('Federal Law No. 6/1973, Article 28', 'القانون الاتحادي رقم 6/1973، المادة 28')
              }
            ].map((item, idx) => (
              <FadeInSection key={idx} delay={idx * 100}>
                <Card 
                  className="p-6 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl hover:shadow-lg transition-all cursor-pointer"
                  onClick={() => handleAskQuestion(item.question)}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                      {item.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    "{item.question}"
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-4">
                    {item.answer}
                  </p>
                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                    <BookOpen className="w-3 h-3 me-1" />
                    <span>{item.citation}</span>
                  </div>
                </Card>
              </FadeInSection>
            ))}
          </div>

          <div className="text-center">
            <Button
              onClick={handleTryNow}
              className="btn-primary px-8 py-6 text-lg font-medium rounded-full"
            >
              {t('Ask Your Question', 'اطرح سؤالك')}
              <ArrowRight className="w-5 h-5 ms-2 rtl:rotate-180" />
            </Button>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-24 px-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
              {t("WHO IT'S FOR", 'لِمَن')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 dark:text-white tracking-tight">
              {t('Built for everyone in the UAE.', 'مصمم للجميع في الإمارات.')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Scale,
                title: t('Lawyers', 'المحامون'),
                body: t('Draft contracts 10x faster. Research case law in seconds. Stay ahead of regulatory changes.', 'صِغ العقود أسرع 10 مرات. ابحث في السوابق القضائية في ثوانٍ. ابقَ متقدماً على التغييرات التنظيمية.')
              },
              {
                icon: Briefcase,
                title: t('Founders', 'المؤسسون'),
                body: t('Navigate company formation, licensing, and employment law without expensive mistakes.', 'تجاوز تأسيس الشركات والتراخيص وقانون العمل بدون أخطاء مكلفة.')
              },
              {
                icon: Globe,
                title: t('Expats', 'المغتربون'),
                body: t('Understand your visa, lease, and employment rights in the language you speak.', 'افهم حقوق تأشيرتك وإيجارك ووظيفتك باللغة التي تتحدثها.')
              },
              {
                icon: Building2,
                title: t('In-House Counsel', 'المستشار القانوني الداخلي'),
                body: t('Get instant answers across Federal, ADGM, and DIFC law. One platform for all three.', 'احصل على إجابات فورية عبر القانون الاتحادي وسوق أبوظبي ومركز دبي. منصة واحدة للثلاثة.')
              },
              {
                icon: Users,
                title: t('Small Businesses', 'الشركات الصغيرة'),
                body: t('Handle contracts, compliance, and disputes without a legal department.', 'تعامل مع العقود والامتثال والنزاعات بدون قسم قانوني.')
              },
              {
                icon: Zap,
                title: t('Investors', 'المستثمرون'),
                body: t('Conduct due diligence on UAE entities. Understand regulatory risk instantly.', 'أجرِ العناية الواجبة على الكيانات الإماراتية. افهم المخاطر التنظيمية فوراً.')
              }
            ].map((persona, idx) => {
              const Icon = persona.icon;
              return (
                <FadeInSection key={idx} delay={idx * 100}>
                  <Card className="p-6 bg-white dark:bg-gray-800 border-0 rounded-2xl hover:shadow-md transition-shadow cursor-pointer">
                    <Icon className="w-8 h-8 text-gray-900 dark:text-white mb-4" strokeWidth={1.5} />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{persona.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{persona.body}</p>
                  </Card>
                </FadeInSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Jurisdiction Coverage Section */}
      <section className="py-24 px-6 bg-white dark:bg-gray-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-blue-500 uppercase tracking-wider mb-4">
              {t('COVERAGE', 'التغطية')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 dark:text-white tracking-tight mb-4">
              {t('Every UAE jurisdiction. One platform.', 'كل اختصاص إماراتي. منصة واحدة.')}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 font-light max-w-3xl mx-auto">
              {t(
                "We don't do generic. Qanun is trained on the actual legislation, regulations, and precedents from each specific UAE legal framework.",
                'لا نقدم محتوى عام. قانون مُدرَّب على التشريعات واللوائح والسوابق الفعلية من كل إطار قانوني إماراتي محدد.'
              )}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              {
                icon: Building2,
                title: 'DIFC',
                subtitle: t('Dubai International Financial Centre', 'مركز دبي المالي العالمي'),
                body: t('Employment Law, Companies Law, Contract Law, Courts Law & Precedents', 'قانون العمل، قانون الشركات، قانون العقود، قانون المحاكم والسوابق')
              },
              {
                icon: Scale,
                title: 'ADGM',
                subtitle: t('Abu Dhabi Global Market', 'سوق أبوظبي العالمي'),
                body: t('Foundations, Funds, Employment Regulations, FSRA Rulebook', 'المؤسسات، الصناديق، لوائح التوظيف، كتيب قواعد FSRA')
              },
              {
                icon: Landmark,
                title: t('UAE Federal Law', 'القانون الاتحادي الإماراتي'),
                subtitle: t('Federal Legislation', 'التشريع الاتحادي'),
                body: t('Civil Code, Commercial Companies Law, Labour Law, Penal Code', 'القانون المدني، قانون الشركات التجارية، قانون العمل، قانون العقوبات')
              },
              {
                icon: Grid3X3,
                title: t('Free Zones', 'المناطق الحرة'),
                subtitle: t('40+ Zones Covered', '40+ منطقة مغطاة'),
                body: t('JAFZA, DMCC, DAFZA, RAKEZ, Meydan, Sharjah FZ, and more', 'جافزا، DMCC، دافزا، راكز، ميدان، المنطقة الحرة بالشارقة، والمزيد')
              },
              {
                icon: Home,
                title: t('Real Estate', 'العقارات'),
                subtitle: t('RERA & Tenancy Law', 'ريرا وقانون الإيجارات'),
                body: t('RERA regulations, Dubai Tenancy Law, Strata Law, SPA disputes', 'لوائح ريرا، قانون الإيجارات في دبي، قانون الطوابق، نزاعات عقود البيع')
              },
              {
                icon: User,
                title: t('Immigration', 'الهجرة'),
                subtitle: t('Visa & Residency', 'التأشيرات والإقامة'),
                body: t('Golden Visa, Green Visa, work permits, residency rules across all Emirates', 'التأشيرة الذهبية، التأشيرة الخضراء، تصاريح العمل، قواعد الإقامة في جميع الإمارات')
              }
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <FadeInSection key={idx} delay={idx * 100}>
                  <Card className="p-6 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl">
                    <Icon className="w-8 h-8 text-gray-900 dark:text-white mb-4" strokeWidth={1.5} />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{item.title}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{item.subtitle}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{item.body}</p>
                  </Card>
                </FadeInSection>
              );
            })}
          </div>

          {/* Featured Language Card */}
          <FadeInSection delay={600}>
            <Card className="p-6 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-2 border-blue-200 dark:border-blue-800 rounded-2xl">
              <div className="flex items-center gap-4">
                <Languages className="w-10 h-10 text-blue-600 dark:text-blue-400" strokeWidth={1.5} />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('Arabic + English', 'العربية + الإنجليزية')}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('Bilingual Intelligence', 'ذكاء ثنائي اللغة')}</p>
                  <p className="text-gray-600 dark:text-gray-300 mt-1">
                    {t('Ask in Arabic, get answers in Arabic. Full legal accuracy in both languages. Launched Phase 1.', 'اسأل بالعربية، احصل على إجابات بالعربية. دقة قانونية كاملة بكلتا اللغتين. تم إطلاق المرحلة الأولى.')}
                  </p>
                </div>
              </div>
            </Card>
          </FadeInSection>

          <p className="text-center text-gray-500 dark:text-gray-400 mt-8">
            {t("Don't see your area? We add new jurisdictions every month.", 'لا ترى مجالك؟ نضيف اختصاصات جديدة كل شهر.')}
            <button className="text-blue-500 hover:text-blue-600 ms-1">
              {t('→ Request a jurisdiction', '→ اطلب اختصاصاً')}
            </button>
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 bg-white dark:bg-gray-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
              {t('WHAT YOU CAN DO', 'ما يمكنك فعله')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 dark:text-white tracking-tight">
              {t('Not just answers. Full legal intelligence.', 'ليس فقط إجابات. ذكاء قانوني كامل.')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: MessageSquare,
                title: t('Ask anything about UAE law', 'اسأل أي شيء عن قانون الإمارات'),
                link: '/chat',
                features: [
                  t('Natural language queries in Arabic or English', 'استفسارات بلغة طبيعية بالعربية أو الإنجليزية'),
                  t('Instant answers with full citations', 'إجابات فورية مع مراجع كاملة'),
                  t('Export conversations as PDF', 'صدّر المحادثات كـ PDF')
                ]
              },
              {
                icon: FileSearch,
                title: t('Smart Contract Reviewer', 'مراجع العقود الذكي'),
                link: '/contract-review',
                features: [
                  t('Paste your contract for AI clause-by-clause review', 'الصق عقدك لمراجعة كل بند بالذكاء الاصطناعي'),
                  t('Risk scores, compliance checks, and rewrite suggestions', 'تقييم المخاطر والامتثال واقتراحات إعادة الصياغة')
                ]
              },
              {
                icon: PenTool,
                title: t('Generate compliant contracts', 'أنشئ عقوداً متوافقة'),
                link: '/contracts',
                features: [
                  t('Employment agreements, NDAs, SHAs, SPAs', 'عقود العمل، اتفاقيات السرية، اتفاقيات المساهمين'),
                  t('Jurisdiction-specific (ADGM, DIFC, Free Zone, Federal)', 'حسب الاختصاص (أبوظبي، دبي، منطقة حرة، اتحادي)')
                ]
              },
              {
                icon: Scale,
                title: t('AI Case Law Search', 'بحث ذكي في السوابق القضائية'),
                link: '/cases',
                features: [
                  t('Search court judgments across UAE, DIFC, and ADGM', 'ابحث في أحكام محاكم الإمارات ودبي وأبوظبي'),
                  t('Key principles and cited laws for each case', 'المبادئ الرئيسية والقوانين المشار إليها لكل قضية')
                ]
              },
              {
                icon: Search,
                title: t('Search the full UAE legal corpus', 'ابحث في المدونة القانونية'),
                link: '/search',
                features: [
                  t('650+ legal sources across DIFC, ADGM, and UAE Federal Law', 'أكثر من 650 مصدر قانوني عبر مركز دبي وأبوظبي والقانون الاتحادي'),
                  t('Filter by jurisdiction, date, topic', 'فلتر حسب الاختصاص والتاريخ والموضوع')
                ]
              },
              {
                icon: Landmark,
                title: t('Compare all 45 UAE free zones', 'قارن المناطق الحرة الـ45'),
                link: '/free-zones',
                features: [
                  t('Side-by-side comparison of costs, visas, and sectors', 'مقارنة جنباً إلى جنب للتكاليف والتأشيرات والقطاعات'),
                  t('Find the perfect zone for your business', 'اعثر على المنطقة المثالية لعملك')
                ]
              },
              {
                icon: BookOpen,
                title: t('Clause Library', 'مكتبة البنود القانونية'),
                link: '/clauses',
                features: [
                  t('Copy-paste ready clauses for UAE contracts', 'بنود جاهزة للنسخ واللصق'),
                  t('Risk-rated by jurisdiction and category', 'مصنفة حسب المخاطر والاختصاص والفئة')
                ]
              },
              {
                icon: Bell,
                title: t('Never miss a law change', 'لا تفوّت أي تغيير قانوني'),
                link: '/alerts',
                features: [
                  t('Live feed of new regulations and court judgments', 'تحديثات مباشرة للوائح والأحكام'),
                  t('Personalized alerts for your practice areas', 'تنبيهات مخصصة لمجالات ممارستك')
                ]
              },
              {
                icon: Shield,
                title: t('AI Contract Negotiation', 'تفاوض العقود بالذكاء الاصطناعي'),
                link: '/negotiate',
                features: [
                  t('Both parties negotiate with AI-mediated fair compromises', 'يتفاوض الطرفان مع حلول عادلة بوساطة الذكاء الاصطناعي'),
                  t('Legal context and risk flags for every position', 'سياق قانوني وتنبيهات لكل موقف')
                ]
              },
              {
                icon: FileSearch,
                title: t('Templates Marketplace', 'سوق القوالب القانونية'),
                link: '/marketplace',
                features: [
                  t('Browse, buy, and sell UAE legal contract templates', 'تصفح واشتري وبيع قوالب العقود القانونية'),
                  t('Verified templates by top UAE legal practitioners', 'قوالب موثقة من أفضل المحامين في الإمارات')
                ]
              },
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <FadeInSection key={idx} delay={idx * 100}>
                  <Card 
                    className="p-6 bg-gray-50 dark:bg-gray-800 border-0 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    onClick={() => navigate(feature.link)}
                    data-testid={`feature-card-${idx}`}
                  >
                    <div className="w-12 h-12 bg-white dark:bg-gray-700 rounded-xl flex items-center justify-center mb-4 shadow-sm">
                      <Icon className="w-6 h-6 text-gray-900 dark:text-white" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{feature.title}</h3>
                    <ul className="space-y-2">
                      {feature.features.map((f, i) => (
                        <li key={i} className="flex items-start text-sm text-gray-600 dark:text-gray-300">
                          <Check className="w-4 h-4 text-blue-500 me-2 mt-0.5 flex-shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                </FadeInSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bilingual Section */}
      <section className="py-24 px-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 dark:text-white mb-2 tracking-tight">
            العربية والإنجليزية
          </h2>
          <p className="text-2xl text-gray-600 dark:text-gray-300 mb-8">
            {t('Arabic and English. Equally fluent.', 'العربية والإنجليزية. بنفس الطلاقة.')}
          </p>
          <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed max-w-2xl mx-auto">
            {t(
              'Many UAE laws exist only in Arabic. Others have conflicting English translations. Qanun AI handles both — fluently — and flags discrepancies that matter.',
              'كثير من قوانين الإمارات موجودة بالعربية فقط. وأخرى لها ترجمات إنجليزية متضاربة. قانون AI يتعامل مع كليهما — بطلاقة — ويُنبّه للتناقضات المهمة.'
            )}
          </p>
        </div>
      </section>

      {/* Trust & Accuracy Section */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
              {t('ACCURACY YOU CAN TRUST', 'دقة يمكنك الوثوق بها')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 dark:text-white mb-4 tracking-tight">
              {t('Every answer cites the law.', 'كل إجابة تستشهد بالقانون.')}
            </h2>
            <p className="text-2xl text-gray-600 dark:text-gray-300 font-light">
              {t('Every answer cites the exact law it\'s based on.', 'كل إجابة تستشهد بالقانون المحدد الذي تستند إليه.')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: BookOpen,
                title: t('Mandatory Citations', 'استشهادات إلزامية'),
                body: t('Every response references the exact Article, Regulation, or Court Judgment it\'s based on.', 'كل إجابة تُشير إلى المادة أو اللائحة أو حكم المحكمة الدقيق الذي تستند إليه.')
              },
              {
                icon: Shield,
                title: t('UAE Data Residency', 'إقامة البيانات في الإمارات'),
                body: t('Your documents stay in the UAE. Hosted on Azure UAE North. We never train on your data.', 'مستنداتك تبقى في الإمارات. مستضافة على Azure UAE North. لا نتدرب أبداً على بياناتك.')
              },
              {
                icon: Check,
                title: t('Lawyer-Validated', 'مُوثَّق من محامين'),
                body: t('Built with input from UAE-qualified lawyers. Legal validation ongoing.', 'بُني بمدخلات من محامين مؤهلين في الإمارات. التحقق القانوني مستمر.')
              }
            ].map((pillar, idx) => {
              const Icon = pillar.icon;
              return (
                <FadeInSection key={idx} delay={idx * 100} className="text-center">
                  <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-gray-900 dark:text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{pillar.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300">{pillar.body}</p>
                </FadeInSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Early Access CTA Section */}
      <section className="py-24 px-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 dark:text-white tracking-tight mb-6">
            {t('Ready to experience smarter legal research?', 'مستعد لتجربة بحث قانوني أذكى؟')}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            {t(
              "We're onboarding our first design partners across UAE law firms and in-house teams.",
              'نحن نضم أول شركائنا من مكاتب المحاماة والفرق القانونية الداخلية في الإمارات.'
            )}
          </p>
          <a
            href="mailto:virajdhawan@gmail.com?subject=Qanun AI Early Access"
            className="inline-flex items-center justify-center px-8 py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-full font-medium text-lg hover:opacity-90 transition-opacity"
          >
            {t('Request Early Access', 'اطلب وصولاً مبكراً')}
          </a>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
              {t('PRICING', 'الأسعار')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 dark:text-white tracking-tight">
              {t('From free forever to enterprise power.', 'من مجاني للأبد إلى قوة المؤسسات.')}
            </h2>

            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1 mt-8">
              <button
                onClick={() => setLandingBilling('monthly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  landingBilling === 'monthly'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {t('Monthly', 'شهري')}
              </button>
              <button
                onClick={() => setLandingBilling('annual')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center ${
                  landingBilling === 'annual'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {t('Annual', 'سنوي')}
                <span className="ms-2 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 font-semibold px-2 py-0.5 rounded-full">
                  {t('Save 2 months', 'وفر شهرين')}
                </span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Free Tier */}
            <FadeInSection delay={0}>
              <Card className="p-8 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl hover:shadow-lg transition-shadow">
                <div className="mb-6">
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('Free', 'مجاني')}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{t('For Everyone', 'للجميع')}</p>
                </div>
                <div className="mb-6">
                  <p className="text-4xl font-semibold text-gray-900 dark:text-white">{t('Free', 'مجاني')}</p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">{t('Forever', 'للأبد')}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    t('10 questions per month', '10 أسئلة شهرياً'),
                    t('Basic legal search', 'بحث قانوني أساسي'),
                    t('Contract template access', 'الوصول لقوالب العقود'),
                    t('Arabic & English support', 'دعم العربية والإنجليزية')
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                      <Check className="w-5 h-5 text-blue-500 me-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={handleTryNow}
                  className="w-full btn-primary rounded-full py-6"
                >
                  {t('Start Free', 'ابدأ مجاناً')}
                </Button>
              </Card>
            </FadeInSection>

            {/* Professional Tier */}
            <FadeInSection delay={100}>
              <Card className="p-8 bg-gray-900 text-white border-0 rounded-2xl relative hover:shadow-xl transition-shadow">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {t('POPULAR', 'الأكثر شيوعاً')}
                  </span>
                </div>
                <div className="mb-6">
                  <h3 className="text-2xl font-semibold">{t('Professional', 'احترافي')}</h3>
                  <p className="text-gray-400">{t('For Solo Lawyers & Consultants', 'للمحامين والمستشارين المستقلين')}</p>
                </div>
                <div className="mb-6">
                  <p className="text-4xl font-semibold">
                    {landingBilling === 'annual' ? t('AED 899', '899 درهم') : t('AED 1,099', '1,099 درهم')}
                  </p>
                  <p className="text-gray-400 text-sm">{t('/month', '/شهرياً')}</p>
                  {landingBilling === 'annual' && (
                    <div className="mt-1">
                      <p className="text-gray-400 text-xs">{t('AED 10,990/year, billed annually', '10,990 درهم/سنة، فوترة سنوية')}</p>
                      <p className="text-green-400 text-xs font-semibold mt-0.5">{t('Save AED 2,198', 'وفر 2,198 درهم')}</p>
                    </div>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    t('Unlimited questions', 'أسئلة غير محدودة'),
                    t('Document analysis & drafting', 'تحليل وصياغة المستندات'),
                    t('Regulatory alerts', 'تنبيهات تنظيمية'),
                    t('Priority AI responses', 'أولوية في استجابات الذكاء الاصطناعي'),
                    t('Export all conversations', 'تصدير جميع المحادثات')
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-300">
                      <Check className="w-5 h-5 text-blue-400 me-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => navigate('/pricing')}
                  className="w-full bg-white text-gray-900 hover:bg-gray-100 rounded-full py-6 transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  {t('Start 14-Day Trial', 'ابدأ تجربة 14 يوماً')}
                </Button>
              </Card>
            </FadeInSection>

            {/* Team Tier */}
            <FadeInSection delay={150}>
              <Card className="p-8 bg-white dark:bg-gray-800 border-2 border-blue-200 dark:border-blue-800 rounded-2xl hover:shadow-lg transition-shadow">
                <div className="mb-6">
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('Team', 'فريق')}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{t('For Law Firms & In-house Teams (2-10 users)', 'لمكاتب المحاماة والفرق القانونية (2-10 مستخدمين)')}</p>
                </div>
                <div className="mb-6">
                  <p className="text-4xl font-semibold text-gray-900 dark:text-white">
                    {landingBilling === 'annual' ? t('AED 599', '599 درهم') : t('AED 729', '729 درهم')}
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">{t('/user/month', '/مستخدم/شهرياً')}</p>
                  {landingBilling === 'annual' && (
                    <div className="mt-1">
                      <p className="text-gray-500 dark:text-gray-400 text-xs">{t('AED 7,190/user/year, billed annually', '7,190 درهم/مستخدم/سنة، فوترة سنوية')}</p>
                      <p className="text-green-500 text-xs font-semibold mt-0.5">{t('Save AED 1,558/user', 'وفر 1,558 درهم/مستخدم')}</p>
                    </div>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    t('Everything in Professional', 'كل ما في الاحترافي'),
                    t('Team workspaces & shared folders', 'مساحات عمل مشتركة'),
                    t('Collaborative document editing', 'تحرير المستندات التعاوني'),
                    t('Admin dashboard', 'لوحة تحكم المشرف'),
                    t('Priority support', 'دعم ذو أولوية')
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                      <Check className="w-5 h-5 text-blue-500 me-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => navigate('/pricing')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full py-6 transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  {t('Start 14-Day Trial', 'ابدأ تجربة 14 يوماً')}
                </Button>
              </Card>
            </FadeInSection>

            {/* Enterprise Tier */}
            <FadeInSection delay={200}>
              <Card className="p-8 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl hover:shadow-lg transition-shadow">
                <div className="mb-6">
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('Enterprise', 'المؤسسات')}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{t('For Firms & Corporates', 'للشركات والمؤسسات')}</p>
                </div>
                <div className="mb-6">
                  <p className="text-4xl font-semibold text-gray-900 dark:text-white">{t('Custom', 'مخصص')}</p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">{t('Pricing', 'التسعير')}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    t('Everything in Professional', 'كل ما في الاحترافي'),
                    t('Team workspaces', 'مساحات عمل الفريق'),
                    t('API access', 'الوصول لـ API'),
                    t('Custom integrations', 'تكاملات مخصصة'),
                    t('Dedicated support', 'دعم مخصص'),
                    t('Priority support', 'دعم ذو أولوية')
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                      <Check className="w-5 h-5 text-blue-500 me-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <a
                  href="mailto:virajdhawan@gmail.com?subject=Qanun AI Enterprise Inquiry"
                  className="w-full inline-flex items-center justify-center border-2 border-gray-900 dark:border-white text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 rounded-full py-6 transition-all hover:scale-[1.02] active:scale-[0.98] font-medium"
                >
                  {t('Contact Sales', 'تواصل مع المبيعات')}
                </a>
              </Card>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-6 bg-white dark:bg-gray-950">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 dark:text-white tracking-tight">
              {t('Questions? We\'ve got answers.', 'أسئلة؟ لدينا الإجابات.')}
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: t('Is this legal advice?', 'هل هذه استشارة قانونية؟'),
                a: t('No. Qanun AI provides legal information and research assistance. For specific legal advice, you should consult a qualified UAE lawyer. Think of us as your research assistant, not your lawyer.', 'لا. قانون AI يقدم معلومات قانونية ومساعدة في البحث. للحصول على مشورة قانونية محددة، يجب استشارة محامٍ مؤهل في الإمارات. فكر بنا كمساعد بحث، وليس محاميك.')
              },
              {
                q: t('How accurate is Qanun AI?', 'ما مدى دقة قانون AI؟'),
                a: t('Every response is backed by citations to official UAE laws, regulations, or court judgments. We don\'t generate answers — we retrieve and explain real legal sources. When we\'re uncertain, we say "I don\'t know" rather than guess.', 'كل إجابة مدعومة باستشهادات من القوانين واللوائح والأحكام القضائية الرسمية في الإمارات. نحن لا نولّد إجابات — بل نسترجع ونشرح مصادر قانونية حقيقية. عندما نكون غير متأكدين، نقول "لا أعرف" بدلاً من التخمين.')
              },
              {
                q: t('Does Qanun AI work in Arabic?', 'هل يعمل قانون AI بالعربية؟'),
                a: t('Yes. Fully. You can ask questions and read answers in Arabic or English. Many UAE laws exist only in Arabic — Qanun AI handles both languages fluently.', 'نعم. بالكامل. يمكنك طرح الأسئلة وقراءة الإجابات بالعربية أو الإنجليزية. كثير من قوانين الإمارات موجودة بالعربية فقط — قانون AI يتعامل مع كلتا اللغتين بطلاقة.')
              },
              {
                q: t('What jurisdictions does Qanun AI cover?', 'ما الاختصاصات القضائية التي يغطيها قانون AI؟'),
                a: t('UAE Federal law, ADGM, DIFC, and all 45+ UAE free zones. We\'re the only platform that understands the full three-layer complexity of UAE law.', 'القانون الاتحادي الإماراتي، سوق أبوظبي العالمي، مركز دبي المالي العالمي، وجميع المناطق الحرة الـ45+. نحن المنصة الوحيدة التي تفهم التعقيد الثلاثي الكامل للقانون الإماراتي.')
              },
              {
                q: t('Can I use Qanun AI for my law firm?', 'هل يمكنني استخدام قانون AI لمكتبي القانوني؟'),
                a: t('Absolutely. Our Professional and Enterprise tiers are built for lawyers. You get unlimited queries, contract drafting, document analysis, and team collaboration features.', 'بالتأكيد. مستويات الاحترافي والمؤسسات مصممة للمحامين. تحصل على استفسارات غير محدودة، وصياغة العقود، وتحليل المستندات، وميزات التعاون الجماعي.')
              },
              {
                q: t('What happens to my uploaded documents?', 'ماذا يحدث للمستندات التي أرفعها؟'),
                a: t('Your documents are encrypted, stored on Azure UAE North (UAE data residency), and never used to train our AI. You can delete them anytime.', 'مستنداتك مشفرة، ومخزنة على Azure UAE North (إقامة بيانات في الإمارات)، ولا تُستخدم أبداً لتدريب الذكاء الاصطناعي. يمكنك حذفها في أي وقت.')
              },
              {
                q: t('Do you replace lawyers?', 'هل تستبدلون المحامين؟'),
                a: t('No — we make them faster. Qanun AI handles research, drafting, and compliance checks so lawyers can focus on strategy and client work. For complex matters, you still need a qualified lawyer.', 'لا — نحن نجعلهم أسرع. قانون AI يتولى البحث والصياغة وفحص الامتثال حتى يتمكن المحامون من التركيز على الاستراتيجية وعمل العملاء. للمسائل المعقدة، لا تزال بحاجة لمحامٍ مؤهل.')
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-gray-50 dark:bg-gray-800 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-6 py-5 text-left rtl:text-right flex justify-between items-center cursor-pointer"
                >
                  <span className="font-medium text-gray-900 dark:text-white">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-300 ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === idx ? 'max-h-96 pb-5' : 'max-h-0'}`}>
                  <div className="px-6">
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 dark:text-white mb-4 tracking-tight">
            {t('Ready to understand UAE law?', 'مستعد لفهم قانون الإمارات؟')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-10">
            {t('Start with 10 free questions. No credit card required.', 'ابدأ بـ 10 أسئلة مجانية. بدون بطاقة ائتمان.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={handleTryNow}
              data-testid="final-cta-button"
              className="btn-primary px-10 py-6 text-lg font-medium rounded-full"
            >
              {t('Try Qanun AI Free', 'جرّب قانون AI مجاناً')}
              <ArrowRight className="w-5 h-5 ms-2 rtl:rotate-180" />
            </Button>
            <a
              href="mailto:virajdhawan@gmail.com?subject=Qanun AI Demo Request"
              className="inline-flex items-center px-10 py-6 text-lg font-medium rounded-full border-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <Play className="w-5 h-5 me-2" />
              {t('Request a Demo', 'اطلب عرضاً توضيحياً')}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center space-x-2 rtl:space-x-reverse mb-4">
                <QanunLogoMinimal className="w-7 h-7 text-white" />
                <span className="text-xl font-semibold">
                  Qanun<span className="font-light text-gray-400 ml-0.5">AI</span>
                </span>
              </div>
              <p className="text-gray-400 text-sm">
                {t('Legal intelligence for the UAE.', 'ذكاء قانوني للإمارات.')}
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">{t('Product', 'المنتج')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><button onClick={() => scrollToSection('features')} className="hover:text-white transition-colors cursor-pointer">{t('Features', 'المميزات')}</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition-colors cursor-pointer">{t('Pricing', 'الأسعار')}</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">{t('Company', 'الشركة')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/about" className="hover:text-white transition-colors cursor-pointer">{t('About Us', 'من نحن')}</a></li>
                <li><a href="mailto:virajdhawan@gmail.com?subject=Contact Qanun AI" className="hover:text-white transition-colors cursor-pointer">{t('Contact', 'تواصل معنا')}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">{t('Legal', 'قانوني')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/terms" className="hover:text-white transition-colors cursor-pointer">{t('Terms of Service', 'شروط الخدمة')}</a></li>
                <li><a href="/privacy" className="hover:text-white transition-colors cursor-pointer">{t('Privacy Policy', 'سياسة الخصوصية')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('Support', 'الدعم')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="mailto:virajdhawan@gmail.com?subject=Qanun AI Support" className="hover:text-white transition-colors cursor-pointer">{t('Email Us', 'راسلنا')}</a></li>
                <li><a href="https://wa.me/971586857370" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">{t('WhatsApp Us', 'واتساب')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('Connect', 'تواصل')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="https://twitter.com/qanunai" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Twitter</a></li>
                <li><a href="https://linkedin.com/company/qanunai" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">LinkedIn</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-500 mb-4 md:mb-0">
              © 2026 Qanun AI. {t('UAE data residency. All rights reserved.', 'إقامة بيانات في الإمارات. جميع الحقوق محفوظة.')}
            </p>
            <button
              onClick={handleLanguageToggle}
              className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer"
            >
              {language === 'en' ? 'العربية' : 'English'} | {language === 'en' ? 'English' : 'العربية'}
            </button>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/971586857370"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        aria-label="Chat on WhatsApp"
        data-testid="whatsapp-float-btn"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </a>

      {/* Custom CSS for animations */}
      <style>{`

        /* WhatsApp floating button */
        .whatsapp-float {
          position: fixed;
          bottom: 24px;
          left: 24px;
          z-index: 9999;
          background: #25D366;
          border-radius: 50%;
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
          text-decoration: none;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        @media (max-width: 768px) {
          .whatsapp-float { bottom: 80px; }
        }
        .whatsapp-float:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(0,0,0,0.3);
        }
        /* Sand Gold accent color */
        .text-gold {
          color: #3B82F6;
        }
        .bg-gold {
          background-color: #3B82F6;
        }
        
        /* Animated background gradient */
        .bg-animated {
          background: linear-gradient(135deg, #FFFFFF 0%, #F0F7FF 50%, #FFFFFF 100%);
          background-size: 200% 200%;
          animation: gradientShift 10s ease infinite;
        }
        
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        /* Primary button styles - Blue */
        .btn-primary {
          background-color: #111827;
          color: white;
          transition: all 200ms ease;
          cursor: pointer;
        }
        .btn-primary:hover {
          background-color: #1f2937;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(17, 24, 39, 0.2);
        }
        .btn-primary:active {
          transform: scale(0.98);
        }
        
        /* Secondary button styles */
        .btn-secondary {
          border: 2px solid #d1d5db;
          color: #111827;
          background-color: transparent;
          transition: all 200ms ease;
          cursor: pointer;
        }
        .btn-secondary:hover {
          background-color: #FAF7F0;
          transform: scale(1.02);
        }
        .btn-secondary:active {
          transform: scale(0.98);
        }
        
        /* Fade-in animation */
        @keyframes fade-in {
          from { 
            opacity: 0; 
            transform: translateY(10px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        .animate-fade-in {
          animation: fade-in 0.4s ease-out forwards;
        }
        
        /* Scale-in animation for modals */
        @keyframes scale-in {
          from { 
            opacity: 0; 
            transform: scale(0.95); 
          }
          to { 
            opacity: 1; 
            transform: scale(1); 
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out forwards;
        }
        
        /* Line clamp utility */
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        /* Smooth transitions for all interactive elements */
        button, a, .cursor-pointer {
          cursor: pointer;
        }
        
        /* FAQ smooth expand/collapse */
        .max-h-0 {
          max-height: 0;
        }
        .max-h-96 {
          max-height: 24rem;
        }
      `}</style>

      {/* Accessibility: prefers-reduced-motion */}
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
          .animate-fade-in, .animate-scale-in, .animate-bounce { animation: none !important; opacity: 1 !important; transform: none !important; }
        }
      `}</style>
    </div>
  );
}
